import type { CabinState, DemoPreset } from "../types/domain";

export const initialCabinState: CabinState = {
  desktop: "ai",
  liteMode: false,
  scriptTrigger: "none",
  dayType: "weekday",
  timeOfDay: "evening",
  weather: "cloudy",
  forecastGoodDays: 2,
  batteryLevel: 80,
  rangeKm: 600,
  fuelLevel: 65,
  fuelRangeKm: 423,
  destinationName: "陆家嘴中心",
  destinationDistanceKm: 12,
  traffic: "light",
  location: "office",
  vehicleDirty: 18,
  parked: false,
  parcelPending: false,
  parcelOnRoute: false,
  carwashCoupon: false,
  parkingQrDetected: false,
  chargerAvailability: 2,
  chargerPrice: 1.56,
  parkingAvailability: 12,
  parkingDistanceM: 620
};

export const demoPresets: DemoPreset[] = [
  {
    id: "eva-space",
    title: "EVA 生活空间",
    description: "共享空间、共享状态与共享经历的五场景交互演示",
    focusService: null,
    statePatch: {
      desktop: "ai",
      scriptTrigger: "eva-space",
      dayType: "weekend",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "home",
      parked: true,
      destinationName: "共同生活空间",
      destinationDistanceKm: 0,
      batteryLevel: 80,
      fuelLevel: 65,
      traffic: "light"
    }
  },
  {
    id: "weekday-parcel",
    title: "下班取快递",
    description: "工作日下班、待取包裹和家附近绑定驿站触发快递助手",
    focusService: "parcel",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "commuteHome",
      weather: "cloudy",
      location: "office",
      parked: false,
      parcelPending: true,
      parcelOnRoute: true,
      traffic: "medium",
      destinationDistanceKm: 18
    }
  },
  {
    id: "weekend-carwash",
    title: "周末洗车",
    description: "顺路门店、优惠和好天气触发洗车管家",
    focusService: "carwash",
    statePatch: {
      desktop: "ai",
      dayType: "weekend",
      timeOfDay: "morning",
      weather: "sunny",
      forecastGoodDays: 4,
      vehicleDirty: 86,
      carwashCoupon: true,
      traffic: "light",
      destinationDistanceKm: 18
    }
  },
  {
    id: "low-battery",
    title: "目的地补能",
    description: "低电量和目的地附近充电站触发充电管家",
    focusService: "charging",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "evening",
      weather: "cloudy",
      batteryLevel: 18,
      rangeKm: 135,
      destinationDistanceKm: 12,
      chargerAvailability: 6,
      chargerPrice: 1.2,
      traffic: "medium"
    }
  },
  {
    id: "destination-parking",
    title: "停车助手",
    description: "停车费用待支付，触发支付宝停车助手",
    focusService: "parking",
    statePatch: {
      desktop: "ai",
      dayType: "weekend",
      timeOfDay: "evening",
      weather: "sunny",
      location: "destination",
      parked: true,
      parkingQrDetected: true,
      destinationDistanceKm: 0,
      parkingAvailability: 28,
      parkingDistanceM: 320,
      traffic: "heavy"
    }
  },
  {
    id: "low-fuel",
    title: "顺路加油",
    description: "低油量和后续行程触发加油授权码",
    focusService: "refuel",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "commuteHome",
      weather: "cloudy",
      batteryLevel: 82,
      fuelLevel: 18,
      destinationDistanceKm: 36,
      traffic: "medium"
    }
  },
  {
    id: "wait-by-detour",
    title: "绕路等人",
    description: "从龙耀路地铁站2号口出发，可选10–13分钟短线或13–16分钟长线，绕行后回到同一站口接人",
    focusService: null,
    statePatch: {
      desktop: "standard",
      scriptTrigger: "wait-by-detour",
      dayType: "weekday",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "destination",
      parked: false,
      destinationName: "龙耀路地铁站2号口",
      destinationDistanceKm: 0,
      batteryLevel: 82,
      fuelLevel: 68,
      vehicleDirty: 18,
      parcelPending: false,
      parcelOnRoute: false,
      carwashCoupon: false,
      parkingQrDetected: false,
      chargerAvailability: 2,
      traffic: "medium"
    }
  },
  {
    id: "xiaohongshu-parking",
    title: "小红书停车对比",
    description: "对比仅使用地图数据与接入小红书到访经验后，逛街找免费停车和送医停车规划的差异",
    focusService: null,
    statePatch: {
      desktop: "ai",
      scriptTrigger: "xiaohongshu-parking",
      dayType: "weekend",
      timeOfDay: "morning",
      weather: "sunny",
      location: "office",
      parked: false,
      destinationName: "前滩太古里",
      destinationDistanceKm: 18,
      batteryLevel: 82,
      fuelLevel: 68,
      vehicleDirty: 18,
      parcelPending: false,
      parcelOnRoute: false,
      carwashCoupon: false,
      parkingQrDetected: false,
      traffic: "light"
    }
  },
  {
    id: "voice-companion",
    title: "音乐搭子",
    description: "脚本专用：通勤音频推荐、播客问答和 AI 内容生成",
    focusService: "charging",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "morning",
      weather: "cloudy",
      location: "office",
      parked: false,
      destinationName: "虹桥火车站",
      destinationDistanceKm: 22,
      batteryLevel: 21,
      rangeKm: 158,
      fuelLevel: 68,
      fuelRangeKm: 442,
      chargerAvailability: 8,
      chargerPrice: 1.18,
      traffic: "medium"
    }
  },
  {
    id: "video-companion",
    title: "视频搭子",
    description: "充电等待 32 分钟，触发视频搭子的推荐、选片辅助和陪伴问答",
    focusService: "charging",
    statePatch: {
      desktop: "ai",
      scriptTrigger: "video-companion",
      dayType: "weekday",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "destination",
      parked: true,
      batteryLevel: 37,
      rangeKm: 286,
      chargerAvailability: 8,
      chargerPrice: 1.2,
      destinationName: "星越超充站 B2",
      destinationDistanceKm: 0,
      traffic: "light"
    }
  },
  {
    id: "tomato-reaction",
    title: "扔番茄",
    description: "独立样例：识别争议剧情后提示互动，连续投掷三次触发金色彩蛋",
    focusService: "charging",
    statePatch: {
      desktop: "ai",
      scriptTrigger: "tomato-reaction",
      dayType: "weekday",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "destination",
      parked: true,
      batteryLevel: 37,
      rangeKm: 286,
      chargerAvailability: 8,
      chargerPrice: 1.2,
      destinationName: "星越超充站 B2",
      destinationDistanceKm: 0,
      traffic: "light"
    }
  },
  {
    id: "movie-gacha",
    title: "选片扭蛋机",
    description: "独立样例：进入视频首页后由 EVA 邀请一起选片，通过扭蛋互动给出最终推荐",
    focusService: null,
    statePatch: {
      desktop: "ai",
      scriptTrigger: "movie-gacha",
      dayType: "weekend",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "home",
      parked: true,
      batteryLevel: 68,
      rangeKm: 510,
      fuelLevel: 65,
      fuelRangeKm: 423,
      destinationName: "家",
      destinationDistanceKm: 0,
      traffic: "light"
    }
  },
  {
    id: "character-voice-gaoqiqiang",
    title: "高启强来电",
    description: "点击后直接播放用户提供的角色台词音频",
    focusService: null,
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "night",
      weather: "cloudy",
      location: "home",
      parked: true,
      destinationName: "家",
      destinationDistanceKm: 0,
      traffic: "light"
    }
  },
  {
    id: "collision-review",
    title: "碰撞回溯",
    description: "脚本专用：模拟碰撞后自动整理行车数据与关键片段",
    focusService: "collision",
    statePatch: {
      desktop: "ai",
      scriptTrigger: "collision-review",
      dayType: "weekday",
      timeOfDay: "evening",
      weather: "cloudy",
      location: "destination",
      parked: true,
      traffic: "medium",
      batteryLevel: 72,
      fuelLevel: 58,
      destinationName: "事故现场",
      destinationDistanceKm: 0
    }
  }
];
